import * as Dialog from '@radix-ui/react-dialog';
import { FiX } from 'react-icons/fi';
import { useState, useEffect } from 'react';
import BorderGradient from 'components/shared/BorderGradient/BorderGradient';

interface SubscriberModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: any;
}

export default function SubscriberModal({ isOpen, onClose, mode, initialData }: SubscriberModalProps) {
  const [formData, setFormData] = useState({
    customerName: initialData?.customerName || '',
    email: initialData?.email || '',
    phoneNumber: initialData?.phoneNumber || '',
    company: initialData?.company || '',
    address: initialData?.address || '',
    subscription: initialData?.subscription || '',
    sendWelcomeEmail: initialData?.sendWelcomeEmail || false,
    forcePasswordChange: initialData?.forcePasswordChange || false,
    twoFactorAuth: initialData?.twoFactorAuth || false,
    twoFactorMethod: initialData?.twoFactorMethod || 'email'
  });

  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData({
        customerName: initialData.customerName || '',
        email: initialData.email || '',
        phoneNumber: initialData.phoneNumber || '',
        company: initialData.company || '',
        address: initialData.address || '',
        subscription: initialData.subscription || '',
        sendWelcomeEmail: initialData.sendWelcomeEmail || false,
        forcePasswordChange: initialData.forcePasswordChange || false,
        twoFactorAuth: initialData.twoFactorAuth || false,
        twoFactorMethod: initialData.twoFactorMethod || 'email'
      });
    }
  }, [initialData, mode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form data:', formData);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content 
          className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-[51] "
          onInteractOutside={(e) => e.preventDefault()}
        >
          <BorderGradient>
            <div className="w-[600px] bg-[#1A1A1A] rounded-lg">
              <div className="flex justify-between items-center p-6 border-b border-white/[0.08]">
                <h2 className="text-xl font-semibold text-white">
                  {mode === 'edit' ? 'Edit Subscriber' : 'Add Subscriber'}
                </h2>
                <button onClick={onClose} className="text-gray-400 hover:text-white">
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Customer Name</label>
                    <input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Phone number</label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Company</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08] resize-none h-[80px]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Subscription</label>
                    <select
                      value={formData.subscription}
                      onChange={(e) => setFormData({ ...formData, subscription: e.target.value })}
                      className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08]"
                    >
                      <option value="">Select subscription</option>
                      <option value="plan1">Plan 1</option>
                      <option value="plan2">Plan 2</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-sm font-medium text-white mb-4">Additional Settings</h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.sendWelcomeEmail}
                        onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                        className="rounded border-gray-400 bg-[#2A2A2A]"
                      />
                      <span className="text-gray-400">Send Welcome email</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.forcePasswordChange}
                        onChange={(e) => setFormData({ ...formData, forcePasswordChange: e.target.checked })}
                        className="rounded border-gray-400 bg-[#2A2A2A]"
                      />
                      <span className="text-gray-400">Force Password change on first login</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.twoFactorAuth}
                        onChange={(e) => setFormData({ ...formData, twoFactorAuth: e.target.checked })}
                        className="rounded border-gray-400 bg-[#2A2A2A]"
                      />
                      <span className="text-gray-400">Two-Factor Authentication (2FA)</span>
                    </label>
                    {formData.twoFactorAuth && (
                      <select
                        value={formData.twoFactorMethod}
                        onChange={(e) => setFormData({ ...formData, twoFactorMethod: e.target.value })}
                        className="w-full bg-[#2A2A2A] rounded-lg px-4 py-2.5 text-white border border-white/[0.08] mt-2"
                      >
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="authenticator">Authenticator App</option>
                      </select>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33]"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </BorderGradient>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
} 